import { ApiError, shortDate, type ActivationGate, type Issue, type RiskException, type StandingWaiver } from './api'
import { bandWord } from '../components/BandChip'

// The written risk acceptance and the contract waiver (release 3.2, founder
// decisions 1 and 2), as the vendor page and the register read them.
//
// # WHAT THEY ARE
//
// Two kinds of record about the vendor as a whole, raised through the same
// route as an exception (POST /v1/exceptions with a kind) and naming no
// finding, gate, parameter or severity:
//
//  - a risk acceptance accepts the vendor's current risk rating in writing,
//    where the approval rules say that rating needs one before activation. It
//    is approved by the Security and Risk head (the CISO or the risk officer),
//    then the vendor's business owner, and runs at most twelve months;
//  - a contract waiver stands in for a contract that is not on file yet, where
//    the approval rules allow one. It is approved by the TPRM lead, then the
//    vendor's business owner, and runs at most ninety days.
//
// Nobody approves their own request. The API refuses one raised where it
// cannot count; this file decides only what to OFFER, from what the activation
// read says, so nobody fills in a form the API would refuse.
//
// # NO CODES ON A SCREEN
//
// The kind, the condition and the refusal codes are keys here and nowhere
// else. Every word a person reads is either the API's or written below.

export type RaiseKind = 'RISK_ACCEPTANCE' | 'CONTRACT_WAIVER'

// The longest term each may run, the same limits the API and the schema hold
// (domain.RiskAcceptanceMaxMonths, domain.ContractWaiverMaxDays).
export const RISK_ACCEPTANCE_MAX_MONTHS = 12
export const CONTRACT_WAIVER_MAX_DAYS = 90

// The conditions of the activation gate each one answers, by the code the API
// keys them on. Used to look the row up, never shown.
const CONDITION_FOR: Record<RaiseKind, string> = {
  RISK_ACCEPTANCE: 'BAND_ACCEPTED',
  CONTRACT_WAIVER: 'CONTRACT_ON_FILE',
}
const DPA_CONDITION = 'DPA_ON_FILE'

// The vendor statuses where raising one still means something: on the way into
// the register, or in it and able to be resumed into it. A vendor on its way
// out, out, or refused is not offered either.
const RAISABLE_STATUS = new Set(['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED'])

// What raising each one is called, and the one sentence beside its form.
export const RAISE_WORDS: Record<RaiseKind, { action: string; heading: string; who: string; submit: string }> = {
  RISK_ACCEPTANCE: {
    action: 'Raise the written risk acceptance',
    heading: 'Written risk acceptance',
    who:
      'The Security and Risk head (the CISO or the risk officer) approves it, then the vendor\'s business owner, ' +
      'for at most 12 months. Nobody approves their own request.',
    submit: 'Raise the risk acceptance',
  },
  CONTRACT_WAIVER: {
    action: 'Ask for a contract waiver',
    heading: 'Contract waiver',
    who:
      'The TPRM lead approves it, then the vendor\'s business owner, for at most 90 days. It stands in for the ' +
      'contract until it expires or the contract is filed. Nobody approves their own request.',
    submit: 'Ask for the waiver',
  },
}

// One thing the approval panel offers to raise, or says is already raised.
export type RaiseOffer = {
  kind: RaiseKind
  label: string
  // A request of this kind already waiting for its sign-offs, so a second
  // one is not offered; the panel says it is waiting instead.
  waiting?: RiskException
}

function conditionState(gate: ActivationGate, code: string): string {
  return gate.conditions.find((c) => c.code === code)?.state ?? ''
}

// Whether a register row is a request of this kind still waiting on its
// sign-offs. A risk acceptance waits for this vendor's current rating only:
// one raised for an earlier rating would not count, so it does not stop a
// fresh one being offered.
function waitingOf(rows: RiskException[], kind: RaiseKind, band: string): RiskException | undefined {
  return rows.find(
    (x) =>
      x.kind === kind &&
      x.status === 'PENDING' &&
      !x.expired &&
      (kind !== 'RISK_ACCEPTANCE' || (x.acceptsBand ?? '') === band),
  )
}

// What the approval panel offers to raise, in the order a person works:
// the risk acceptance, then the contract waiver.
//
//  - Only to a reader who may raise one (risk.accept); the caller passes it.
//  - The risk acceptance only while its condition is outstanding AND the
//    rules in force say this rating needs one.
//  - The contract waiver only while the contract condition is outstanding,
//    the rules allow a waiver, and no waiver already stands in.
//  - Where one of that kind is already waiting for its sign-offs, it is
//    returned with waiting set rather than offered again.
export function raiseOffers(
  gate: ActivationGate,
  opts: { canRaise: boolean; status: string; rows?: RiskException[] | null },
): RaiseOffer[] {
  if (!opts.canRaise || !RAISABLE_STATUS.has(opts.status)) return []
  const rows = opts.rows ?? []
  const out: RaiseOffer[] = []
  if (gate.acceptanceRequired && conditionState(gate, CONDITION_FOR.RISK_ACCEPTANCE) === 'outstanding') {
    out.push({
      kind: 'RISK_ACCEPTANCE',
      label: RAISE_WORDS.RISK_ACCEPTANCE.action,
      waiting: waitingOf(rows, 'RISK_ACCEPTANCE', gate.band),
    })
  }
  if (
    gate.contractWaiverAllowed &&
    !gate.contractWaiver &&
    conditionState(gate, CONDITION_FOR.CONTRACT_WAIVER) === 'outstanding'
  ) {
    out.push({
      kind: 'CONTRACT_WAIVER',
      label: RAISE_WORDS.CONTRACT_WAIVER.action,
      waiting: waitingOf(rows, 'CONTRACT_WAIVER', gate.band),
    })
  }
  return out
}

// ------------------------------------------------------------------ expiry

function utcToday(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10)
}

// The dates the expiry may take, worked out on the UTC day as the API works
// them out (it truncates now to the UTC day): tomorrow at the earliest, and at
// the latest twelve months or ninety days on. The default is the longest term,
// which is the one a person asks for unless they have a reason to ask for less.
// Worked on the local day instead, a person in India before half past five in
// the morning would be offered a last day the API refuses.
export function expiryLimits(kind: RaiseKind, now: Date = new Date()): { min: string; max: string; initial: string } {
  const today = utcToday(now)
  const min = new Date(today)
  min.setUTCDate(min.getUTCDate() + 1)
  const max = new Date(today)
  if (kind === 'RISK_ACCEPTANCE') max.setUTCMonth(max.getUTCMonth() + RISK_ACCEPTANCE_MAX_MONTHS)
  else max.setUTCDate(max.getUTCDate() + CONTRACT_WAIVER_MAX_DAYS)
  return { min: isoDay(min), max: isoDay(max), initial: isoDay(max) }
}

// The longest term in words, for the expiry field's hint.
export function maxTermWords(kind: RaiseKind): string {
  return kind === 'RISK_ACCEPTANCE' ? 'at most 12 months' : 'at most 90 days'
}

// ------------------------------------------------------------------ the form

export type RaiseDraft = {
  justification: string
  compensatingControl: string
  ownerUserId: string
  expiresOn: string
}

export function emptyDraft(kind: RaiseKind, owner: string, now: Date = new Date()): RaiseDraft {
  return { justification: '', compensatingControl: '', ownerUserId: owner, expiresOn: expiryLimits(kind, now).initial }
}

// The checks the API makes, made first so a person is told before the round
// trip. The field names are the API's own issue fields, so a 422 and this list
// share one summary.
export function checkRaise(kind: RaiseKind, d: RaiseDraft, now: Date = new Date()): Issue[] {
  const out: Issue[] = []
  if (!d.justification.trim()) {
    out.push({
      field: 'justification',
      code: 'REQUIRED',
      detail:
        kind === 'RISK_ACCEPTANCE'
          ? 'Say why this rating is being accepted.'
          : 'Say why the contract cannot be filed yet.',
    })
  }
  if (!d.compensatingControl.trim()) {
    out.push({
      field: 'compensatingControl',
      code: 'REQUIRED',
      detail: 'Name what is in place instead. Without it this is an unmanaged gap.',
    })
  }
  if (!d.ownerUserId.trim()) {
    out.push({ field: 'ownerUserId', code: 'REQUIRED', detail: 'Somebody owns this until it expires.' })
  }
  const { min, max } = expiryLimits(kind, now)
  if (!d.expiresOn) {
    out.push({ field: 'expiresOn', code: 'REQUIRED', detail: 'Choose when it ends. Without an expiry nobody revisits it.' })
  } else if (d.expiresOn < min) {
    // D20 of the date rules (lib/dateRules.ts), in the server's words.
    out.push({ field: 'expiresOn', code: 'IN_THE_PAST', detail: 'Expiry must be after today.' })
  } else if (d.expiresOn > max) {
    out.push({
      field: 'expiresOn',
      code: 'TOO_LONG',
      detail:
        kind === 'RISK_ACCEPTANCE'
          ? 'A risk acceptance runs for at most 12 months. Choose ' + shortDate(max) + ' or earlier.'
          : 'A contract waiver runs for at most 90 days. Choose ' + shortDate(max) + ' or earlier.',
    })
  }
  return out
}

// The title the register lists it under. Written here rather than asked for:
// the form asks for the reason, and a title somebody types for a record of a
// fixed kind is a second, drifting name for the same thing.
export function raiseTitle(kind: RaiseKind, vendorName: string, band: string): string {
  const who = vendorName.trim() || 'this vendor'
  return kind === 'RISK_ACCEPTANCE'
    ? 'Written risk acceptance for ' + who + ', ' + ratingPhrase(band)
    : 'Contract waiver for ' + who
}

// ------------------------------------------------------------------ words

const KIND_NAME: Record<string, string> = {
  EXCEPTION: 'Exception',
  RISK_ACCEPTANCE: 'Risk acceptance',
  CONTRACT_WAIVER: 'Contract waiver',
}

// A record's kind as a label: the API's own name where it sent one, else the
// name for the code. An unknown code reads as an exception, which is what the
// register held before there were kinds, and never as itself.
export function kindLabel(x: { kind?: string; kindName?: string }): string {
  if (x.kindName && x.kindName.trim()) return x.kindName.trim()
  return KIND_NAME[(x.kind ?? '').toUpperCase()] ?? 'Exception'
}

export function isContractWaiver(x: { kind?: string }): boolean {
  return (x.kind ?? '').toUpperCase() === 'CONTRACT_WAIVER'
}

export function isRiskAcceptance(x: { kind?: string }): boolean {
  return (x.kind ?? '').toUpperCase() === 'RISK_ACCEPTANCE'
}

// A contract waiver that stands in for a contract now: approved and not past
// its expiry. The register counts these on its Contract waivers tile.
export function waiverStands(x: { kind?: string; status: string; expired: boolean }): boolean {
  return isContractWaiver(x) && x.status === 'APPROVED' && !x.expired
}

// Whether a record is about the vendor as a whole rather than one gap.
export function isVendorWide(x: { kind?: string }): boolean {
  return isContractWaiver(x) || isRiskAcceptance(x)
}

// The rating a risk acceptance accepts, as a word. A vendor without enough
// answers for a band is not yet rated, and the API treats it as Critical.
export function ratingWords(band: string | undefined | null): string {
  const b = (band ?? '').trim().toUpperCase()
  if (b === '' || b === 'INSUFFICIENT_EVIDENCE') return 'Not yet rated'
  const w = bandWord(b)
  return w === 'Not scored' ? 'Not yet rated' : w
}

// The accepted rating as a short phrase beside a label: "Critical rating", or
// "Not yet rated" alone.
export function ratingPhrase(band: string | undefined | null): string {
  const r = ratingWords(band)
  return r === 'Not yet rated' ? r : r + ' rating'
}

// The standing waiver, as the vendor page says it until the contract is filed.
export function waiverLine(w: StandingWaiver): string {
  const reason = w.reason.trim()
  return 'Contract waived until ' + shortDate(w.expiresOn) + (reason ? ': ' + reason : '')
}

export const PINNED_SENTENCE =
  'This vendor was sent for approval under the earlier approval rules and finishes under them.'

// The data processing agreement condition, as the gate read it, for the vendor
// page. Null where the gate did not carry it (an API from before 3.2).
export function dpaCondition(gate: ActivationGate | null | undefined): { state: string; name: string; detail: string } | null {
  const c = gate?.conditions.find((x) => x.code === DPA_CONDITION)
  return c ? { state: c.state, name: c.name, detail: c.detail } : null
}

// The DPA's state as a word for the overview's key facts.
export function dpaWord(state: string): string {
  switch (state) {
    case 'met':
      return 'In order'
    case 'outstanding':
      return 'Outstanding'
    case 'not required':
      return 'Not required here'
  }
  return 'Not read'
}

// ------------------------------------------------------------------ refusals

// The refusals a signature or a raise can meet that are answers rather than
// failures: the API says in a sentence why this person may not, and the
// sentence is what a person reads. The code is a key here and never shown.
const REFUSAL_CODES = new Set([
  'FORBIDDEN',
  'NOT_THIS_OFFICE',
  'BUSINESS_OWNER_ONLY',
  'NAMED_THEMSELVES',
  'SELF_APPROVAL',
  'SIGNER_ALREADY_SIGNED',
  'STAGE_NOT_AVAILABLE',
  'NOT_PENDING_APPROVAL',
  'ALREADY_DECIDED',
  'NOT_NEEDED',
  'APPROVAL_IN_FLIGHT',
])

// The API's sentence for a refusal of that kind, or null for anything else (a
// failure, which keeps its code and reference for support).
export function refusalMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  if (err.status !== 403 && err.status !== 409) return null
  if (!REFUSAL_CODES.has(err.code)) return null
  const m = err.message.trim()
  return m === '' ? null : m
}
